/**
 * e-MSS QueryClient
 *
 * Cache volátil em memória (RAM) — nunca persiste em disco.
 * Cada dado some ao fechar a aba ou fazer logout.
 *
 * TTLs por recurso:
 *   - Dados estáticos (metadata, catalog): 15 min
 *   - Dados de leitura lenta (teams, leagues): 5 min
 *   - Dados de leitura moderada (events, user): 2 min
 *   - Dados dinâmicos (feed, notifications): 30-60 s
 */

import { QueryClient } from '@tanstack/react-query';

// Backoff exponencial com teto em 30s
function exponentialDelay(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 30000);
}

// Não faz retry em erros de cliente (4xx) — só em erros de servidor/rede
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    if (status >= 400 && status < 500) return false; 
  }
  return failureCount < 3; // Aumentado para 3 retentativas para 5xx / network errors
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,       // 2 min — dados envelhecem mais rápido no mobile
      gcTime: 30 * 60 * 1000,          // Mantém em memória por 30 min
      retry: shouldRetry,
      retryDelay: exponentialDelay,
      // ── Otimização de Egress ─────────────────────────────────────────────
      // refetchOnWindowFocus DESABILITADO globalmente para reduzir egress Supabase.
      // Com 9+ hooks montados no EventDetailsPageV2, cada troca de aba antes
      // disparava 9+ requests simultâneos. Hooks que precisam de atualização ao
      // foco (ex: useEventLapRecords) devem declarar refetchOnWindowFocus: true.
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,        // Mantido: refetch ao recuperar conexão (Wi-Fi/dados)
      refetchOnMount: true,            // Mantido: verifica dados ao montar componente
    },
    mutations: {
      retry: (failureCount: number, error: any) => {
        // Retenta mutações apenas se for 503 ou erro de rede (safe to retry idempotent actions usually)
        if (error?.status === 503) return failureCount < 2;
        return false;
      },
    },
  },
});

// TTLs exportados para uso nos hooks — Cache mais agressivo para solidez
export const TTL = {
  STATIC:  60 * 60 * 1000,  // catalog, metadata — 1 hora
  SLOW:    15 * 60 * 1000,  // teams, leagues — 15 min
  MODERATE: 5 * 60 * 1000,  // events, user/me — 5 min
  FAST:     2 * 60 * 1000,  // feed do console — 2 min
  REALTIME: 60 * 1000,       // notifications — 1 min
};
